import * as React from 'react';
import styles from './styles.module.scss';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import Input from '@components/Input';
import Button, { BUTTON_TYPE } from '@components/Button';
import Select from 'react-select';
import HeaderOnBoardingWrapper from '@components/HeaderOnBoardingWrapper';
import InputCheckBox, { CHECKBOX_MODE } from '@components/InputCheckbox';
import Spinner from '@components/Spinner';
import platform from 'platform';

export type User = {
  birthday: string;
  language: string;
  gender: string;
  country: string;
  voiceProficiency: string;
  voiceType: string;
  agreeTerms: boolean;
  agreeTerms2: boolean;
};

type Dirty = {
  birthday: boolean;
  language: boolean;
  gender: boolean;
  country: boolean;
  voiceProficiency: boolean;
  voiceType: boolean;
  agreeTerms: boolean;
  agreeTerms2: boolean;
};
type Props = {
  isLoading: boolean;
  step: number;

  error: { type: string; message: string };
  setStep: (step: number) => void;
  onSubmit: ({
    birthday,
    language,
    country,
    gender,
    voiceProficiency,
    voiceType,
    agreeTerms,
    agreeTerms2,
  }: User) => void;
};

export default function CompleteRegistrationForm({
  isLoading,
  onSubmit,
  step,
  setStep,
}: Props) {
  const [isIOs, setIsIOs] = React.useState<any>();
  const [isAndroid, setIsAndroid] = React.useState<any>();
  const {
    register: registerSecondForm,
    handleSubmit: handleSubmitStep1,
    watch: watchSecondForm,
    control,
    formState: { errors: errorsSecondForm },
  } = useForm({ mode: 'onSubmit', shouldFocusError: false });

  const birthday = watchSecondForm('birthday');
  const gender = watchSecondForm('gender');
  const language = watchSecondForm('language');
  const country = watchSecondForm('country');
  const voiceProficiency = watchSecondForm('voiceProficiency');
  const voiceType = watchSecondForm('voiceType');
  const agree = watchSecondForm('agreeTerms');
  const secondAgree = watchSecondForm('agreeTerms2');

  React.useEffect(() => {
    if (platform?.os) {
      setIsIOs(platform?.os?.family?.includes('iOS'));
    }
    if (platform?.os) {
      setIsAndroid(platform?.os?.family?.includes('Android'));
    }
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [partialUser, setPartialUser] = React.useState<Partial<User>>({});
  const [isDirty, setIsDirty] = React.useState<Dirty>({
    birthday: false,
    language: false,
    gender: false,
    country: false,
    voiceProficiency: false,
    voiceType: false,
    agreeTerms: false,
    agreeTerms2: false,
  });

  const { t } = useTranslation(['signUp', 'countries']);

  const { ref: birthdayRef, ...birthdayProps } = registerSecondForm(
    'birthday',
    {
      required: t('signUp:form.errorsState.required.firstname'),
    }
  );

  const { ref: agreeTermsRef, ...agreeTermsProps } = registerSecondForm(
    'agreeTerms',
    {
      required: t('signUp:form.errorsState.required.generic'),
    }
  );

  const { ref: agreeTerms2Ref, ...agreeTerms2Props } = registerSecondForm(
    'agreeTerms2',
    {
      required: t('signUp:form.errorsState.required.generic'),
    }
  );

  // function handleOnSubmit({ agreeTerms }: UserSubmit) {
  //   setIsDirty({
  //     ...isDirty,
  //     agreeTerms: agreeTerms ?? false,
  //   } as Dirty);

  //   onSubmit({
  //     ...partialUser,
  //     agreeTerms,
  //   } as User);
  // }

  function createDownloadLinkPDF(
    prefix: string,
    language: string,
    nameFile: string,
    atLastPage?: boolean
  ) {
    const splitLang = language ? language.split('-')[0] : 'en';
    return `/${prefix}-${nameFile}-${splitLang}.pdf${
      atLastPage ? '#page=17' : ''
    }`;
  }

  // function createPrivacyPolicyLink(language: string) {
  //   switch (language) {
  //     case 'en-EN':
  //       return 'https://translated.com/confidentiality';
  //     case 'it-IT':
  //       return 'https://translated.com/riservatezza';
  //     case 'es-ES':
  //       return 'https://translated.com/confidencialidad';
  //     case 'de-DE':
  //       return 'https://translated.com/datenschutz';
  //     case 'fr-FR':
  //       return 'https://translated.com/confidentialite';
  //     default:
  //       return 'https://translated.com/confidentiality';
  //   }
  // }

  function handleNextStep2({
    birthday,
    gender,
    language,
    country,
    voiceProficiency,
    voiceType,
    agreeTerms,
    agreeTerms2,
  }: {
    birthday: string;
    gender: string;
    language: string;
    country: string;
    voiceProficiency: string;
    voiceType: string;
    agreeTerms: boolean;
    agreeTerms2: boolean;
  }) {
    setPartialUser({
      birthday,
      language,
      gender,
      country,
      voiceProficiency,
      voiceType,
      agreeTerms,
      agreeTerms2,
    });

    setIsDirty({
      birthday: birthday.length > 0,
      language: language.length > 0,
      gender: gender.length > 0,
      country: country.length > 0,
      voiceProficiency: voiceProficiency.length > 0,
      voiceType: voiceType.length > 0,
      agreeTerms: agreeTerms ?? false,
      agreeTerms2: agreeTerms2 ?? false,
    } as Dirty);

    onSubmit({
      birthday,
      language,
      gender,
      country,
      voiceProficiency,
      voiceType,
      agreeTerms,
      agreeTerms2,
    } as User);
    // setStep(step + 1);
  }

  const customStylesLanguage = {
    container: (base: any) => ({
      ...base,
      '&:not(:last-child)': {
        marginBottom: '16px !important',
      },
    }),
    control: (base: any, state: any) => ({
      ...base,
      marginBottom: '0 !important',
      color: '#000',
      border: '1px solid var(--neutral300)',
      borderColor: 'transparent',
      marginTop: '4px',
      borderRadius: '4px',
      backgroundColor: state.isSelected ? '#f1f1f1' : '#fff',
      boxShadow: state.isFocused
        ? '0 0 0 2px var(--blue) !important'
        : errorsSecondForm.language
        ? '0 0 0 2px var(--red)'
        : '0px 1px 4px rgba(0, 0, 0, 0.04)',
      '&:focus': {
        boxShadow: '0 0 0 1px #cccccc',
        border: 'none',
        borderRadius: '4px',
      },
      '&:hover': {
        border: '1px solid var(--neutral400)',
        boxShadow: 'none',
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      marginBottom: '0px !important',
      height: '48px',
      borderColor: '#cccccc',
      paddingTop: '0',
      paddingBottom: '0',
    }),
    placeholder: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      fontSize: '16px',
      color: 'var(--neutral600)',
    }),
    input: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      '& > div': {
        marginBottom: '0 !important',
      },
    }),
    indicatorSeparator: (base: any, state: any) => ({
      ...base,
      display: language ? 'block' : 'none',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      // display: 'none',
    }),
    singleValue: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
      padding: '4px 0',
    }),
  };

  const customStylesCountry = {
    container: (base: any) => ({
      ...base,
      '&:not(:last-child)': {
        marginBottom: '16px !important',
      },
    }),
    control: (base: any, state: any) => ({
      ...base,
      marginBottom: '0 !important',
      color: '#000',
      border: '1px solid var(--neutral300)',
      marginTop: '4px',
      borderColor: 'transparent',
      borderRadius: '4px',
      backgroundColor: state.isSelected ? '#f1f1f1' : '#fff',
      boxShadow: state.isFocused
        ? '0 0 0 2px var(--blue) !important'
        : errorsSecondForm.country
        ? '0 0 0 2px var(--red)'
        : '0px 1px 4px rgba(0, 0, 0, 0.04)',
      '&:focus': {
        boxShadow: '0 0 0 1px #cccccc',
        border: 'none',
        borderRadius: '4px',
      },
      '&:hover': {
        border: '1px solid var(--neutral400)',
        boxShadow: 'none',
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      marginBottom: '0px !important',
      height: '48px',
      borderColor: '#cccccc',
      paddingTop: '0',
      paddingBottom: '0',
    }),
    placeholder: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      fontSize: '16px',
      color: 'var(--neutral600)',
    }),
    input: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      '& > div': {
        marginBottom: '0 !important',
      },
    }),
    indicatorSeparator: (base: any, state: any) => ({
      ...base,
      display: country ? 'block' : 'none',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      // display: 'none',
    }),
    singleValue: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
      padding: '4px 0',
    }),
    menuList: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'transparent',
    }),
  };

  const customStylesVoice = {
    container: (base: any) => ({
      ...base,
      '&:not(:last-child)': {
        marginBottom: '16px !important',
      },
    }),
    control: (base: any, state: any) => ({
      ...base,
      marginBottom: '0 !important',
      marginTop: '4px',
      color: '#000',
      border: '1px solid var(--neutral300)',
      borderColor: 'transparent',
      borderRadius: '4px',
      backgroundColor: state.isSelected ? '#f1f1f1' : '#fff',
      boxShadow: state.isFocused
        ? '0 0 0 2px var(--blue) !important'
        : errorsSecondForm.voiceProficiency
        ? '0 0 0 2px var(--red)'
        : '0px 1px 4px rgba(0, 0, 0, 0.04)',
      '&:focus': {
        boxShadow: '0 0 0 1px #cccccc',
        border: 'none',
        borderRadius: '4px',
      },
      '&:hover': {
        border: '1px solid var(--neutral400)',
        boxShadow: 'none',
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      marginBottom: '0px !important',
      height: '48px',
      borderColor: '#cccccc',
      paddingTop: '0',
      paddingBottom: '0',
    }),
    placeholder: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      fontSize: '16px',
      color: 'var(--neutral600)',
    }),
    input: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      '& > div': {
        marginBottom: '0 !important',
      },
    }),
    indicatorSeparator: (base: any, state: any) => ({
      ...base,
      display: voiceProficiency ? 'block' : 'none',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      // display: 'none',
    }),
    singleValue: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
      padding: '4px 0',
    }),
  };

  const customStylesVoiceType = {
    container: (base: any) => ({
      ...base,
      '&:not(:last-child)': {
        marginBottom: '16px !important',
      },
    }),
    control: (base: any, state: any) => ({
      ...base,
      marginBottom: '0 !important',
      marginTop: '4px',
      color: '#000',
      border: '1px solid var(--neutral300)',
      borderColor: 'transparent',
      borderRadius: '4px',
      backgroundColor: state.isSelected ? '#f1f1f1' : '#fff',
      boxShadow: state.isFocused
        ? '0 0 0 2px var(--blue) !important'
        : errorsSecondForm.voiceType
        ? '0 0 0 2px var(--red)'
        : '0px 1px 4px rgba(0, 0, 0, 0.04)',
      '&:focus': {
        boxShadow: '0 0 0 1px #cccccc',
        border: 'none',
        borderRadius: '4px',
      },
      '&:hover': {
        border: '1px solid var(--neutral400)',
        boxShadow: 'none',
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      marginBottom: '0px !important',
      height: '48px',
      borderColor: '#cccccc',
      paddingTop: '0',
      paddingBottom: '0',
    }),
    placeholder: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      fontSize: '16px',
      color: 'var(--neutral600)',
    }),
    input: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      '& > div': {
        marginBottom: '0 !important',
      },
    }),
    indicatorSeparator: (base: any, state: any) => ({
      ...base,
      display: voiceType ? 'block' : 'none',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      // display: 'none',
    }),
    singleValue: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
      padding: '4px 0',
    }),
  };

  const customStylesGender = {
    container: (base: any) => ({
      ...base,
      '&:not(:last-child)': {
        marginBottom: '16px !important',
      },
    }),
    control: (base: any, state: any) => ({
      ...base,
      marginBottom: '0 !important',
      marginTop: '4px',
      color: '#000',
      border: '1px solid var(--neutral300)',
      borderColor: 'transparent',
      borderRadius: '4px',
      backgroundColor: state.isSelected ? '#f1f1f1' : '#fff',
      boxShadow: state.isFocused
        ? '0 0 0 2px var(--blue) !important'
        : errorsSecondForm.gender
        ? '0 0 0 2px var(--red)'
        : '0px 1px 4px rgba(0, 0, 0, 0.04)',
      '&:focus': {
        boxShadow: '0 0 0 1px #cccccc',
        border: 'none',
        borderRadius: '4px',
      },
      '&:hover': {
        border: '1px solid var(--neutral400)',
        boxShadow: 'none',
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      marginBottom: '0px !important',
      height: '48px',
      borderColor: '#cccccc',
      paddingTop: '0',
      paddingBottom: '0',
    }),
    placeholder: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      fontSize: '16px',
      color: 'var(--neutral600)',
    }),
    input: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      marginBottom: '0 !important',
      '& > div': {
        marginBottom: '0 !important',
      },
    }),
    indicatorSeparator: (base: any, state: any) => ({
      ...base,
      display: gender ? 'block' : 'none',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      // display: 'none',
    }),
    singleValue: (base: any) => ({
      ...base,
      fontSize: '16px',
      marginBottom: '0 !important',
      padding: '4px 0',
    }),
  };

  const placeholderColor = !birthday
    ? 'var(--neutral300)'
    : 'var(--neutral600)';

  const voiceProficiencySelect = [
    { label: t('signUp:form.select.voiceProficiency.no'), value: 'no' },
    {
      label: t('signUp:form.select.voiceProficiency.diction'),
      value: 'diction',
    },
    {
      label: t('signUp:form.select.voiceProficiency.professional'),
      value: 'professional',
    },
  ];

  const voiceTypeSelect = [
    { label: t('signUp:form.select.voiceType.young'), value: 'young' },
    {
      label: t('signUp:form.select.voiceType.middle'),
      value: 'middle',
    },
    {
      label: t('signUp:form.select.voiceType.senior'),
      value: 'senior',
    },
    {
      label: t('signUp:form.select.voiceType.pediatric'),
      value: 'pediatric',
    },
  ];

  const genderSelect = [
    { value: 'male', label: t('signUp:form.select.gender.male') },
    { value: 'female', label: t('signUp:form.select.gender.female') },
    { value: 'notBinary', label: t('signUp:form.select.gender.notBinary') },
  ];

  const languagesSelect = [
    { value: 'it-IT', label: t('signUp:form.select.languages.italian') },
    { value: 'en-EN', label: t('signUp:form.select.languages.english') },
    { value: 'fr-FR', label: t('signUp:form.select.languages.french') },
    { value: 'de-DE', label: t('signUp:form.select.languages.german') },
    { value: 'es-ES', label: t('signUp:form.select.languages.spanish') },
  ];

  const countrySelect = [
    { label: t('countries:AF'), value: 'AF', callingCodes: ['93'] },
    { label: t('countries:AX'), value: 'AX', callingCodes: ['358'] },
    { label: t('countries:AL'), value: 'AL', callingCodes: ['355'] },
    { label: t('countries:DZ'), value: 'DZ', callingCodes: ['213'] },
    { label: t('countries:AS'), value: 'AS', callingCodes: ['1684'] },
    { label: t('countries:AD'), value: 'AD', callingCodes: ['376'] },
    { label: t('countries:AO'), value: 'AO', callingCodes: ['244'] },
    { label: t('countries:AI'), value: 'AI', callingCodes: ['1264'] },
    { label: t('countries:AQ'), value: 'AQ', callingCodes: ['672'] },
    { label: t('countries:AG'), value: 'AG', callingCodes: ['1268'] },
    { label: t('countries:AR'), value: 'AR', callingCodes: ['54'] },
    { label: t('countries:AM'), value: 'AM', callingCodes: ['374'] },
    { label: t('countries:AW'), value: 'AW', callingCodes: ['297'] },
    { label: t('countries:AU'), value: 'AU', callingCodes: ['61'] },
    { label: t('countries:AT'), value: 'AT', callingCodes: ['43'] },
    { label: t('countries:AZ'), value: 'AZ', callingCodes: ['994'] },
    { label: t('countries:BS'), value: 'BS', callingCodes: ['1242'] },
    { label: t('countries:BH'), value: 'BH', callingCodes: ['973'] },
    { label: t('countries:BD'), value: 'BD', callingCodes: ['880'] },
    { label: t('countries:BB'), value: 'BB', callingCodes: ['1246'] },
    { label: t('countries:BY'), value: 'BY', callingCodes: ['375'] },
    { label: t('countries:BE'), value: 'BE', callingCodes: ['32'] },
    { label: t('countries:BZ'), value: 'BZ', callingCodes: ['501'] },
    { label: t('countries:BJ'), value: 'BJ', callingCodes: ['229'] },
    { label: t('countries:BM'), value: 'BM', callingCodes: ['1441'] },
    { label: t('countries:BT'), value: 'BT', callingCodes: ['975'] },
    {
      label: t('countries:BO'),
      value: 'BO',
      callingCodes: ['591'],
    },
    {
      label: t('countries:BQ'),
      value: 'BQ',
      callingCodes: ['5997'],
    },
    { label: t('countries:BA'), value: 'BA', callingCodes: ['387'] },
    { label: t('countries:BW'), value: 'BW', callingCodes: ['267'] },
    { label: t('countries:BV'), value: 'BV', callingCodes: [''] },
    { label: t('countries:BR'), value: 'BR', callingCodes: ['55'] },
    {
      label: t('countries:IO'),
      value: 'IO',
      callingCodes: ['246'],
    },
    {
      label: t('countries:UM'),
      value: 'UM',
      callingCodes: [''],
    },
    { label: t('countries:VG'), value: 'VG', callingCodes: ['1284'] },
    { label: t('countries:VI'), value: 'VI', callingCodes: ['1 340'] },
    { label: t('countries:BN'), value: 'BN', callingCodes: ['673'] },
    { label: t('countries:BG'), value: 'BG', callingCodes: ['359'] },
    { label: t('countries:BF'), value: 'BF', callingCodes: ['226'] },
    { label: t('countries:BI'), value: 'BI', callingCodes: ['257'] },
    { label: t('countries:KH'), value: 'KH', callingCodes: ['855'] },
    { label: t('countries:CM'), value: 'CM', callingCodes: ['237'] },
    { label: t('countries:CA'), value: 'CA', callingCodes: ['1'] },
    { label: t('countries:CV'), value: 'CV', callingCodes: ['238'] },
    { label: t('countries:KY'), value: 'KY', callingCodes: ['1345'] },
    { label: t('countries:CF'), value: 'CF', callingCodes: ['236'] },
    { label: t('countries:TD'), value: 'TD', callingCodes: ['235'] },
    { label: t('countries:CL'), value: 'CL', callingCodes: ['56'] },
    { label: t('countries:CN'), value: 'CN', callingCodes: ['86'] },
    { label: t('countries:CX'), value: 'CX', callingCodes: ['61'] },
    { label: t('countries:CC'), value: 'CC', callingCodes: ['61'] },
    { label: t('countries:CO'), value: 'CO', callingCodes: ['57'] },
    { label: t('countries:KM'), value: 'KM', callingCodes: ['269'] },
    { label: t('countries:CG'), value: 'CG', callingCodes: ['242'] },
    {
      label: t('countries:CD'),
      value: 'CD',
      callingCodes: ['243'],
    },
    { label: t('countries:CK'), value: 'CK', callingCodes: ['682'] },
    { label: t('countries:CR'), value: 'CR', callingCodes: ['506'] },
    { label: t('countries:HR'), value: 'HR', callingCodes: ['385'] },
    { label: t('countries:CU'), value: 'CU', callingCodes: ['53'] },
    { label: t('countries:CW'), value: 'CW', callingCodes: ['599'] },
    { label: t('countries:CY'), value: 'CY', callingCodes: ['357'] },
    { label: t('countries:CZ'), value: 'CZ', callingCodes: ['420'] },
    { label: t('countries:DK'), value: 'DK', callingCodes: ['45'] },
    { label: t('countries:DJ'), value: 'DJ', callingCodes: ['253'] },
    { label: t('countries:DM'), value: 'DM', callingCodes: ['1767'] },
    {
      label: t('countries:DO'),
      value: 'DO',
      callingCodes: ['1809', '1829', '1849'],
    },
    { label: t('countries:EC'), value: 'EC', callingCodes: ['593'] },
    { label: t('countries:EG'), value: 'EG', callingCodes: ['20'] },
    { label: t('countries:SV'), value: 'SV', callingCodes: ['503'] },
    { label: t('countries:GQ'), value: 'GQ', callingCodes: ['240'] },
    { label: t('countries:ER'), value: 'ER', callingCodes: ['291'] },
    { label: t('countries:EE'), value: 'EE', callingCodes: ['372'] },
    { label: t('countries:ET'), value: 'ET', callingCodes: ['251'] },
    {
      label: t('countries:FK'),
      value: 'FK',
      callingCodes: ['500'],
    },
    { label: t('countries:FO'), value: 'FO', callingCodes: ['298'] },
    { label: t('countries:FJ'), value: 'FJ', callingCodes: ['679'] },
    { label: t('countries:FI'), value: 'FI', callingCodes: ['358'] },
    { label: t('countries:FR'), value: 'FR', callingCodes: ['33'] },
    { label: t('countries:GF'), value: 'GF', callingCodes: ['594'] },
    { label: t('countries:PF'), value: 'PF', callingCodes: ['689'] },
    { label: t('countries:TF'), value: 'TF', callingCodes: [''] },
    { label: t('countries:GA'), value: 'GA', callingCodes: ['241'] },
    { label: t('countries:GM'), value: 'GM', callingCodes: ['220'] },
    { label: t('countries:GE'), value: 'GE', callingCodes: ['995'] },
    { label: t('countries:DE'), value: 'DE', callingCodes: ['49'] },
    { label: t('countries:GH'), value: 'GH', callingCodes: ['233'] },
    { label: t('countries:GI'), value: 'GI', callingCodes: ['350'] },
    { label: t('countries:GR'), value: 'GR', callingCodes: ['30'] },
    { label: t('countries:GL'), value: 'GL', callingCodes: ['299'] },
    { label: t('countries:GD'), value: 'GD', callingCodes: ['1473'] },
    { label: t('countries:GP'), value: 'GP', callingCodes: ['590'] },
    { label: t('countries:GU'), value: 'GU', callingCodes: ['1671'] },
    { label: t('countries:GT'), value: 'GT', callingCodes: ['502'] },
    { label: t('countries:GG'), value: 'GG', callingCodes: ['44'] },
    { label: t('countries:GN'), value: 'GN', callingCodes: ['224'] },
    { label: t('countries:GW'), value: 'GW', callingCodes: ['245'] },
    { label: t('countries:GY'), value: 'GY', callingCodes: ['592'] },
    { label: t('countries:HT'), value: 'HT', callingCodes: ['509'] },
    {
      label: t('countries:HM'),
      value: 'HM',
      callingCodes: [''],
    },
    { label: t('countries:VA'), value: 'VA', callingCodes: ['379'] },
    { label: t('countries:HN'), value: 'HN', callingCodes: ['504'] },
    { label: t('countries:HK'), value: 'HK', callingCodes: ['852'] },
    { label: t('countries:HU'), value: 'HU', callingCodes: ['36'] },
    { label: t('countries:IS'), value: 'IS', callingCodes: ['354'] },
    { label: t('countries:IN'), value: 'IN', callingCodes: ['91'] },
    { label: t('countries:ID'), value: 'ID', callingCodes: ['62'] },
    { label: t('countries:CI'), value: 'CI', callingCodes: ['225'] },
    { label: t('countries:IR'), value: 'IR', callingCodes: ['98'] },
    { label: t('countries:IQ'), value: 'IQ', callingCodes: ['964'] },
    { label: t('countries:IE'), value: 'IE', callingCodes: ['353'] },
    { label: t('countries:IM'), value: 'IM', callingCodes: ['44'] },
    { label: t('countries:IL'), value: 'IL', callingCodes: ['972'] },
    { label: t('countries:IT'), value: 'IT', callingCodes: ['39'] },
    { label: t('countries:JM'), value: 'JM', callingCodes: ['1876'] },
    { label: t('countries:JP'), value: 'JP', callingCodes: ['81'] },
    { label: t('countries:JE'), value: 'JE', callingCodes: ['44'] },
    { label: t('countries:JO'), value: 'JO', callingCodes: ['962'] },
    { label: t('countries:KZ'), value: 'KZ', callingCodes: ['76', '77'] },
    { label: t('countries:KE'), value: 'KE', callingCodes: ['254'] },
    { label: t('countries:KI'), value: 'KI', callingCodes: ['686'] },
    { label: t('countries:KW'), value: 'KW', callingCodes: ['965'] },
    { label: t('countries:KG'), value: 'KG', callingCodes: ['996'] },
    {
      label: t('countries:LA'),
      value: 'LA',
      callingCodes: ['856'],
    },
    { label: t('countries:LV'), value: 'LV', callingCodes: ['371'] },
    { label: t('countries:LB'), value: 'LB', callingCodes: ['961'] },
    { label: t('countries:LS'), value: 'LS', callingCodes: ['266'] },
    { label: t('countries:LR'), value: 'LR', callingCodes: ['231'] },
    { label: t('countries:LY'), value: 'LY', callingCodes: ['218'] },
    { label: t('countries:LI'), value: 'LI', callingCodes: ['423'] },
    { label: t('countries:LT'), value: 'LT', callingCodes: ['370'] },
    { label: t('countries:LU'), value: 'LU', callingCodes: ['352'] },
    { label: t('countries:MO'), value: 'MO', callingCodes: ['853'] },
    {
      label: t('countries:MK'),
      value: 'MK',
      callingCodes: ['389'],
    },
    { label: t('countries:MG'), value: 'MG', callingCodes: ['261'] },
    { label: t('countries:MW'), value: 'MW', callingCodes: ['265'] },
    { label: t('countries:MY'), value: 'MY', callingCodes: ['60'] },
    { label: t('countries:MV'), value: 'MV', callingCodes: ['960'] },
    { label: t('countries:ML'), value: 'ML', callingCodes: ['223'] },
    { label: t('countries:MT'), value: 'MT', callingCodes: ['356'] },
    { label: t('countries:MH'), value: 'MH', callingCodes: ['692'] },
    { label: t('countries:MQ'), value: 'MQ', callingCodes: ['596'] },
    { label: t('countries:MR'), value: 'MR', callingCodes: ['222'] },
    { label: t('countries:MU'), value: 'MU', callingCodes: ['230'] },
    { label: t('countries:YT'), value: 'YT', callingCodes: ['262'] },
    { label: t('countries:MX'), value: 'MX', callingCodes: ['52'] },
    {
      label: t('countries:FM'),
      value: 'FM',
      callingCodes: ['691'],
    },
    { label: t('countries:MD'), value: 'MD', callingCodes: ['373'] },
    { label: t('countries:MC'), value: 'MC', callingCodes: ['377'] },
    { label: t('countries:MN'), value: 'MN', callingCodes: ['976'] },
    { label: t('countries:ME'), value: 'ME', callingCodes: ['382'] },
    { label: t('countries:MS'), value: 'MS', callingCodes: ['1664'] },
    { label: t('countries:MA'), value: 'MA', callingCodes: ['212'] },
    { label: t('countries:MZ'), value: 'MZ', callingCodes: ['258'] },
    { label: t('countries:MM'), value: 'MM', callingCodes: ['95'] },
    { label: t('countries:NA'), value: 'NA', callingCodes: ['264'] },
    { label: t('countries:NR'), value: 'NR', callingCodes: ['674'] },
    { label: t('countries:NP'), value: 'NP', callingCodes: ['977'] },
    { label: t('countries:NL'), value: 'NL', callingCodes: ['31'] },
    { label: t('countries:NC'), value: 'NC', callingCodes: ['687'] },
    { label: t('countries:NZ'), value: 'NZ', callingCodes: ['64'] },
    { label: t('countries:NI'), value: 'NI', callingCodes: ['505'] },
    { label: t('countries:NE'), value: 'NE', callingCodes: ['227'] },
    { label: t('countries:NG'), value: 'NG', callingCodes: ['234'] },
    { label: t('countries:NU'), value: 'NU', callingCodes: ['683'] },
    { label: t('countries:NF'), value: 'NF', callingCodes: ['672'] },
    {
      label: t('countries:KP'),
      value: 'KP',
      callingCodes: ['850'],
    },
    { label: t('countries:MP'), value: 'MP', callingCodes: ['1670'] },
    { label: t('countries:NO'), value: 'NO', callingCodes: ['47'] },
    { label: t('countries:OM'), value: 'OM', callingCodes: ['968'] },
    { label: t('countries:PK'), value: 'PK', callingCodes: ['92'] },
    { label: t('countries:PW'), value: 'PW', callingCodes: ['680'] },
    { label: t('countries:PS'), value: 'PS', callingCodes: ['970'] },
    { label: t('countries:PA'), value: 'PA', callingCodes: ['507'] },
    { label: t('countries:PG'), value: 'PG', callingCodes: ['675'] },
    { label: t('countries:PY'), value: 'PY', callingCodes: ['595'] },
    { label: t('countries:PE'), value: 'PE', callingCodes: ['51'] },
    { label: t('countries:PH'), value: 'PH', callingCodes: ['63'] },
    { label: t('countries:PN'), value: 'PN', callingCodes: ['64'] },
    { label: t('countries:PL'), value: 'PL', callingCodes: ['48'] },
    { label: t('countries:PT'), value: 'PT', callingCodes: ['351'] },
    { label: t('countries:PR'), value: 'PR', callingCodes: ['1787', '1939'] },
    { label: t('countries:QA'), value: 'QA', callingCodes: ['974'] },
    { label: t('countries:XK'), value: 'XK', callingCodes: ['383'] },
    { label: t('countries:RE'), value: 'RE', callingCodes: ['262'] },
    { label: t('countries:RO'), value: 'RO', callingCodes: ['40'] },
    { label: t('countries:RU'), value: 'RU', callingCodes: ['7'] },
    { label: t('countries:RW'), value: 'RW', callingCodes: ['250'] },
    { label: t('countries:BL'), value: 'BL', callingCodes: ['590'] },
    {
      label: t('countries:SH'),
      value: 'SH',
      callingCodes: ['290'],
    },
    { label: t('countries:KN'), value: 'KN', callingCodes: ['1869'] },
    { label: t('countries:LC'), value: 'LC', callingCodes: ['1758'] },
    { label: t('countries:MF'), value: 'MF', callingCodes: ['590'] },
    { label: t('countries:PM'), value: 'PM', callingCodes: ['508'] },
    {
      label: t('countries:VC'),
      value: 'VC',
      callingCodes: ['1784'],
    },
    { label: t('countries:WS'), value: 'WS', callingCodes: ['685'] },
    { label: t('countries:SM'), value: 'SM', callingCodes: ['378'] },
    { label: t('countries:ST'), value: 'ST', callingCodes: ['239'] },
    { label: t('countries:SA'), value: 'SA', callingCodes: ['966'] },
    { label: t('countries:SN'), value: 'SN', callingCodes: ['221'] },
    { label: t('countries:RS'), value: 'RS', callingCodes: ['381'] },
    { label: t('countries:SC'), value: 'SC', callingCodes: ['248'] },
    { label: t('countries:SL'), value: 'SL', callingCodes: ['232'] },
    { label: t('countries:SG'), value: 'SG', callingCodes: ['65'] },
    { label: t('countries:SX'), value: 'SX', callingCodes: ['1721'] },
    { label: t('countries:SK'), value: 'SK', callingCodes: ['421'] },
    { label: t('countries:SI'), value: 'SI', callingCodes: ['386'] },
    { label: t('countries:SB'), value: 'SB', callingCodes: ['677'] },
    { label: t('countries:SO'), value: 'SO', callingCodes: ['252'] },
    { label: t('countries:ZA'), value: 'ZA', callingCodes: ['27'] },
    {
      label: t('countries:GS'),
      value: 'GS',
      callingCodes: ['500'],
    },
    { label: t('countries:KR'), value: 'KR', callingCodes: ['82'] },
    { label: t('countries:SS'), value: 'SS', callingCodes: ['211'] },
    { label: t('countries:ES'), value: 'ES', callingCodes: ['34'] },
    { label: t('countries:LK'), value: 'LK', callingCodes: ['94'] },
    { label: t('countries:SD'), value: 'SD', callingCodes: ['249'] },
    { label: t('countries:SR'), value: 'SR', callingCodes: ['597'] },
    { label: t('countries:SJ'), value: 'SJ', callingCodes: ['4779'] },
    { label: t('countries:SZ'), value: 'SZ', callingCodes: ['268'] },
    { label: t('countries:SE'), value: 'SE', callingCodes: ['46'] },
    { label: t('countries:CH'), value: 'CH', callingCodes: ['41'] },
    { label: t('countries:SY'), value: 'SY', callingCodes: ['963'] },
    { label: t('countries:TW'), value: 'TW', callingCodes: ['886'] },
    { label: t('countries:TJ'), value: 'TJ', callingCodes: ['992'] },
    {
      label: t('countries:TZ'),
      value: 'TZ',
      callingCodes: ['255'],
    },
    { label: t('countries:TH'), value: 'TH', callingCodes: ['66'] },
    { label: t('countries:TL'), value: 'TL', callingCodes: ['670'] },
    { label: t('countries:TG'), value: 'TG', callingCodes: ['228'] },
    { label: t('countries:TK'), value: 'TK', callingCodes: ['690'] },
    { label: t('countries:TO'), value: 'TO', callingCodes: ['676'] },
    { label: t('countries:TT'), value: 'TT', callingCodes: ['1868'] },
    { label: t('countries:TN'), value: 'TN', callingCodes: ['216'] },
    { label: t('countries:TR'), value: 'TR', callingCodes: ['90'] },
    { label: t('countries:TM'), value: 'TM', callingCodes: ['993'] },
    { label: t('countries:TC'), value: 'TC', callingCodes: ['1649'] },
    { label: t('countries:TV'), value: 'TV', callingCodes: ['688'] },
    { label: t('countries:UG'), value: 'UG', callingCodes: ['256'] },
    { label: t('countries:UA'), value: 'UA', callingCodes: ['380'] },
    { label: t('countries:AE'), value: 'AE', callingCodes: ['971'] },
    {
      label: t('countries:GB'),
      value: 'GB',
      callingCodes: ['44'],
    },
    { label: t('countries:US'), value: 'US', callingCodes: ['1'] },
    { label: t('countries:UY'), value: 'UY', callingCodes: ['598'] },
    { label: t('countries:UZ'), value: 'UZ', callingCodes: ['998'] },
    { label: t('countries:VU'), value: 'VU', callingCodes: ['678'] },
    {
      label: t('countries:VE'),
      value: 'VE',
      callingCodes: ['58'],
    },
    { label: t('countries:VN'), value: 'VN', callingCodes: ['84'] },
    { label: t('countries:WF'), value: 'WF', callingCodes: ['681'] },
    { label: t('countries:EH'), value: 'EH', callingCodes: ['212'] },
    { label: t('countries:YE'), value: 'YE', callingCodes: ['967'] },
    { label: t('countries:ZM'), value: 'ZM', callingCodes: ['260'] },
    { label: t('countries:ZW'), value: 'ZW', callingCodes: ['263'] },
  ];

  return (
    <div className={styles.container}>
      {step === 1 ? (
        <>
          <HeaderOnBoardingWrapper title={t('signUp:titlePage.step1')} />
          <form onSubmit={handleSubmitStep1(handleNextStep2 as any)}>
            <fieldset>
              <Input
                className={`${birthday ? 'notEmpty' : ''} ${
                  isIOs ? 'isIos' : ''
                } ${isAndroid ? 'isAndroid' : ''}`}
                style={{ color: placeholderColor }}
                hasValue={isDirty?.birthday ?? false}
                label={t('signUp:form.labels.birthday')}
                type="date"
                // onFocus={onFocusDate}
                inputRef={birthdayRef}
                placeholder={t('signUp:form.placeholder.date')}
                {...birthdayProps}
                // onBlur={onBlurDate}
                error={errorsSecondForm?.birthday?.message}
              />

              <Controller
                control={control}
                name="gender"
                rules={{
                  required: {
                    value: true,
                    message: t('signUp:form.errorsState.required.generic'),
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <span>{t('signUp:form.labels.gender')}</span>
                    <Select
                      options={genderSelect}
                      isClearable={true}
                      noOptionsMessage={() =>
                        t('signUp:form.select.notFound.gender')
                      }
                      value={
                        value ? genderSelect.find((c) => c.value === value) : ''
                      }
                      styles={customStylesGender}
                      onChange={(val: any) => onChange(val?.value)}
                      placeholder={t('signUp:form.placeholder.gender')}
                    />
                  </>
                )}
              />
              {errorsSecondForm.gender && (
                <p
                  className={styles.errorMessage}
                  style={{
                    fontWeight: 500,
                    marginTop: '-8px',
                    marginBottom: '16px',
                  }}
                >
                  {errorsSecondForm.gender.message}
                </p>
              )}

              <Controller
                control={control}
                name="country"
                rules={{
                  required: {
                    value: true,
                    message: t('signUp:form.errorsState.required.generic'),
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <>
                    <span>{t('signUp:form.labels.country')}</span>
                    <Select
                      options={countrySelect}
                      isClearable={true}
                      noOptionsMessage={() =>
                        t('signUp:form.select.notFound.country')
                      }
                      value={
                        value
                          ? countrySelect.find((c) => c.value === value)
                          : ''
                      }
                      styles={customStylesCountry}
                      onChange={(val: any) => onChange(val?.value)}
                      placeholder={t('signUp:form.placeholder.country')}
                    />
                  </>
                )}
              />
              {errorsSecondForm.country && (
                <p
                  className={styles.errorMessage}
                  style={{
                    fontWeight: 500,
                    marginTop: '-8px',
                    marginBottom: '16px',
                  }}
                >
                  {errorsSecondForm.country.message}
                </p>
              )}

              <Controller
                control={control}
                name="language"
                rules={{
                  required: {
                    value: true,
                    message: t('signUp:form.errorsState.required.generic'),
                  },
                }}
                render={({ field: { onChange, value } }: any) => (
                  <>
                    <span>{t('signUp:form.labels.language')}</span>
                    <Select
                      options={languagesSelect}
                      isClearable={true}
                      noOptionsMessage={() =>
                        t('signUp:form.select.notFound.language')
                      }
                      value={
                        value
                          ? languagesSelect.find((c) => c.value === value)
                          : ''
                      }
                      styles={customStylesLanguage}
                      onChange={(val: any) => onChange(val?.value)}
                      placeholder={t('signUp:form.placeholder.language')}
                    />
                  </>
                )}
              />
              {errorsSecondForm.language && (
                <p
                  className={styles.errorMessage}
                  style={{
                    fontWeight: 500,
                    marginTop: '-8px',
                    marginBottom: '16px',
                  }}
                >
                  {errorsSecondForm.language.message}
                </p>
              )}

              <Controller
                control={control}
                name="voiceProficiency"
                rules={{
                  required: {
                    value: true,
                    message: t('signUp:form.errorsState.required.generic'),
                  },
                }}
                render={({ field: { onChange, value } }: any) => (
                  <>
                    <span>{t('signUp:form.labels.voiceProficiency')}</span>
                    <Select
                      options={voiceProficiencySelect}
                      isClearable={true}
                      noOptionsMessage={() =>
                        t('signUp:form.select.notFound.voiceProficiency')
                      }
                      value={
                        value
                          ? voiceProficiencySelect.find(
                              (c) => c.value === value
                            )
                          : ''
                      }
                      styles={customStylesVoice}
                      onChange={(val: any) => onChange(val?.value)}
                      placeholder={t(
                        'signUp:form.placeholder.voiceProficiency'
                      )}
                    />
                  </>
                )}
              />
              {errorsSecondForm.voiceProficiency && (
                <p
                  className={styles.errorMessage}
                  style={{
                    fontWeight: 500,
                    marginTop: '-8px',
                    marginBottom: '16px',
                  }}
                >
                  {errorsSecondForm.voiceProficiency.message}
                </p>
              )}

              <Controller
                control={control}
                name="voiceType"
                rules={{
                  required: {
                    value: true,
                    message: t('signUp:form.errorsState.required.generic'),
                  },
                }}
                render={({ field: { onChange, value } }: any) => (
                  <>
                    <span>{t('signUp:form.labels.voiceType')}</span>
                    <Select
                      options={voiceTypeSelect}
                      isClearable={true}
                      noOptionsMessage={() =>
                        t('signUp:form.select.notFound.voiceType')
                      }
                      value={
                        value
                          ? voiceTypeSelect.find((c) => c.value === value)
                          : ''
                      }
                      styles={customStylesVoiceType}
                      onChange={(val: any) => onChange(val?.value)}
                      placeholder={t('signUp:form.placeholder.voiceType')}
                    />
                  </>
                )}
              />
              {errorsSecondForm.voiceType && (
                <p
                  className={styles.errorMessage}
                  style={{
                    fontWeight: 500,
                    marginTop: '-8px',
                    marginBottom: '16px',
                  }}
                >
                  {errorsSecondForm.voiceType.message}
                </p>
              )}
              {/* </fieldset>

            <div className={styles.agreeFooter}>
              <div className={styles.agreeFooterWrap}> */}
              <div className={styles.agreement}>
                <InputCheckBox
                  mode={CHECKBOX_MODE.DEFAULT}
                  hasValue={isDirty?.agreeTerms ?? false}
                  // label={t('signUp:form.labels.agreeTerms')}
                  label={
                    <p style={{ fontSize: '14px' }}>
                      {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        <Trans
                          defaults={t('signUp:form.labels.agreeTerms')}
                          components={{
                            link1: (
                              <a
                                target="_blank"
                                rel="noreferrer"
                                href={createDownloadLinkPDF(
                                  'MasterContract',
                                  language,
                                  'confirmClauses'
                                )}
                              ></a>
                            ),
                            link2: (
                              <a
                                target="_blank"
                                rel="noreferrer"
                                href={createDownloadLinkPDF(
                                  'Privacy',
                                  language,
                                  'translated'
                                )}
                              ></a>
                            ),
                          }}
                        />
                      }
                    </p>
                  }
                  type="checkbox"
                  inputRef={agreeTermsRef}
                  {...agreeTermsProps}
                  error={errorsSecondForm?.agreeTerms?.message}
                />
                <InputCheckBox
                  mode={CHECKBOX_MODE.DEFAULT}
                  hasValue={isDirty?.agreeTerms2 ?? false}
                  label={
                    <p style={{ fontSize: '14px' }}>
                      {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        <Trans
                          defaults={t('signUp:form.labels.agreeTerms2')}
                          components={{
                            link1: (
                              <a
                                target="_blank"
                                rel="noreferrer"
                                href={createDownloadLinkPDF(
                                  'MasterContract',
                                  language,
                                  'confirmClauses',
                                  true
                                )}
                              ></a>
                            ),
                          }}
                        />
                      }
                    </p>
                  }
                  // t('signUp:form.labels.agreeTerms2')}
                  type="checkbox"
                  inputRef={agreeTerms2Ref}
                  {...agreeTerms2Props}
                  error={errorsSecondForm?.agreeTerms2?.message}
                />
              </div>
              <Button
                type={BUTTON_TYPE.PRIMARY}
                disabled={
                  !birthday ||
                  !gender ||
                  !language ||
                  !country ||
                  !voiceProficiency ||
                  !voiceType ||
                  !agree ||
                  !secondAgree
                }
                fullWidth
              >
                <div className={!isLoading ? styles.label : styles.hide}>
                  {t('signUp:form.labels.createAccount')}
                </div>
                {isLoading && (
                  <div className={styles.spinnerContainer}>
                    <Spinner isWhite isMid />
                  </div>
                )}
              </Button>
            </fieldset>
            {/* </div>
            </div> */}
          </form>
        </>
      ) : (
        ''
      )}

      {/* {step === 2 ? (
        <form
          className={styles.agreeForm}
          onSubmit={handleSubmitFinal(handleOnSubmit as any)}
          noValidate
        >
   

          <div className={styles.scrollableWrap}>
            <p className={styles.agreementTitle}>
              {t('signUp:form.agreementPolicy.title')}
            </p>
            <div className={styles.agreementContent}>
              <div className={styles.innerContent}>
                <p className={styles.titleParagraph}>
                  <strong>
                    {t('signUp:form.agreementPolicy.preamble.title')}
                  </strong>
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t('signUp:form.agreementPolicy.preamble.content')}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.title')}
                </p>
                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause1.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause1.description'
                      )}
                    />
                  }
                </p>

                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.specifics.clause1.content.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause2.title')}
                </p>
                <p>
                  {t(
                    'signUp:form.agreementPolicy.specifics.clause2.description'
                  )}
                </p>

                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.specifics.clause2.content.list'
                    )}
                    components={{
                      link1: (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href="https://www.matedub.com"
                        ></a>
                      ),
                    }}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause3.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause3.description'
                      )}
                    />
                  }
                </p>
                <div className={styles.clause34}>
                  <p className={styles.titleParagraph}>
                    {t('signUp:form.agreementPolicy.specifics.clause3-4.title')}
                  </p>{' '}
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause3-4.description'
                      )}
                    />
                  }
                </div>
                <div className={styles.clause35}>
                  <p className={styles.titleParagraph}>
                    {t('signUp:form.agreementPolicy.specifics.clause3-5.title')}
                  </p>{' '}
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause3-5.description'
                      )}
                    />
                  }
                </div>

                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause4.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause4.description'
                      )}
                    />
                  }
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.specifics.clause4.content.list4-1.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.specifics.clause4.content.list4-1.listDash'
                    )}
                    components={[
                      <ul className={`${styles.list} ${styles.dashed}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.specifics.clause4.content.list4-2.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause5.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause5.description'
                      )}
                    />
                  }
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.specifics.clause5.content.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause6.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause6.description'
                      )}
                    />
                  }
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.specifics.clause6.content.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }

                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause7.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause7.description'
                      )}
                    />
                  }
                </p>

                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause9.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause9.description'
                      )}
                    />
                  }
                </p>

                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.specifics.clause11.title')}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.specifics.clause11.description'
                      )}
                    />
                  }
                </p>

                <p className={styles.titleSection}>
                  {t('signUp:form.agreementPolicy.TeC.title')}
                </p>
                <p className={styles.titleParagraph}>
                  {t('signUp:form.agreementPolicy.TeC.preamble.title')}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.preamble.content'
                    )}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list1.title'
                  )}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list1-1.title'
                      )}
                    />
                  }
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list1-1.list'
                    )}
                    components={[
                      <ul className={`${styles.list} ${styles.dotted}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list1-2.title'
                    )}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list2.title'
                  )}
                </p>
                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list2.link'
                      )}
                      components={{
                        link1: (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href="https://www.matedub.com"
                          ></a>
                        ),
                      }}
                    />
                  }
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list2.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list3.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list3.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list4.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list4.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list5.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list5.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list6.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list6.list'
                    )}
                    components={[
                      <ul className={`${styles.list} ${styles.list6}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list7.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list7.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list8.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list8.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list8.link'
                    )}
                    components={{
                      link1: <a href="mailto:info@matedub.com"></a>,
                    }}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list8.list-from-8-3'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list9.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list9.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list10.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list10.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list11.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list11.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list12.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list12.list'
                    )}
                    components={[
                      <ul className={`${styles.list} ${styles.list12}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list12.list12-3.title'
                    )}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list12.list12-3.list'
                    )}
                    components={[
                      <ul className={`${styles.list} ${styles.dotted}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list12.list-from-12-4'
                    )}
                    components={[
                      <ul className={`${styles.list} ${styles.list12}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list13.title'
                  )}
                </p>

                <p>
                  {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Trans
                      defaults={t(
                        'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list13.link'
                      )}
                      components={{
                        link1: <a href="mailto:info@matedub.com"></a>,
                      }}
                    />
                  }
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list13.list13-2.title'
                    )}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list13.list13-2.list'
                    )}
                    components={[
                      <ul className={`${styles.list} ${styles.dotted}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list13.list13-2.title13-3'
                    )}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list14.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list14.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list15.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list15.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list16.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list16.list'
                    )}
                    components={[
                      <ul className={styles.list}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                <p className={styles.titleParagraph}>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list17.title'
                  )}
                </p>
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list17.list'
                    )}
                    components={[
                      <ul className={`${styles.list}`}>
                        {['item1', 'item2', 'item3'].map((item: any) => (
                          <li>{item}</li>
                        ))}
                      </ul>,
                    ]}
                  />
                }
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  <Trans
                    defaults={t(
                      'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.list17.link'
                    )}
                    components={{
                      link1: <a href="mailto:info@matedub.com"></a>,
                    }}
                  />
                }
                <p>
                  {t(
                    'signUp:form.agreementPolicy.TeC.subjectOfTheTerms.finalNote'
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.agreeFooter}>
            <div className={styles.agreeFooterWrap}>
              <InputCheckBox
                mode={CHECKBOX_MODE.DEFAULT}
                hasValue={isDirty?.agreeTerms ?? false}
                label={t('signUp:form.labels.agreeTerms')}
                type="checkbox"
                inputRef={agreeTermsRef}
                {...agreeTermsProps}
                error={errorsThirdForm?.agreeTerms?.message}
              />
 
              <Button type={BUTTON_TYPE.PRIMARY} disabled={!agree} fullWidth>
                <div className={!isLoading ? styles.label : styles.hide}>
                  {t('signUp:form.labels.createAccount')}
                </div>
                {isLoading && (
                  <div className={styles.spinnerContainer}>
                    <Spinner isWhite isMid />
                  </div>
                )}
              </Button>
            </div>
          </div>
        </form>
      ) : (
        ''
      )} */}
    </div>
  );
}
